import './InviteStudentsModal.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';

import ErrorInfo from 'components/ErrorInfo/ErrorInfo';
import { Input } from '@trackman/web-shared-components';
import { Modal } from '@trackman/web-shared-components';
import NewInvitesList from 'components/Organization/NewInvitesList/NewInvitesList';
import NoInvitesLeft from 'components/Organization/NoInvitesLeft/NoInvitesLeft';
import NumberOfInvitesLeft from 'components/Organization/NumberOfInvitesLeft/NumberOfInvitesLeft';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as SendIcon } from 'images/icons/Send.svg';
import { isValidEmail } from 'utilities/inputValidation';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';

export const InviteStudentsModal = observer(() => {
  const { t } = useTranslation();
  const iss = useStores().inviteStudentsStore;
  const orgStore = useStores().organizationsStore;
  const cs = useStores().classesStore;
  const [inputsVisible, setInputsVisible] = useState(true);
  const [emailError, setEmailError] = useState<string>('');
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);

  const isFormValid = useMemo(
    () => !!iss.newInvite.name && !!iss.newInvite.email && !emailError,
    [iss.newInvite.name, iss.newInvite.email, emailError]
  );

  useEffect(() => {
    const classItem = cs.classesList.find((classItem) => classItem.id === cs.selectedClassId);
    iss.selectedClass = { id: cs.selectedClassId, name: classItem ? classItem.name : '' };
  }, [iss, cs.classesList, cs.selectedClassId]);

  const handleSaveStudent = useCallback(() => {
    if (!isValidEmail(iss.newInvite.email)) {
      setEmailError(t('EmailFormatIsNotValid', { ns: 'studentsPage' }));
      return;
    }

    if (iss.newInvites.find((invite) => invite.email === iss.newInvite.email)) {
      setEmailError(t('ThisEmailIsAlreadyAdded', { ns: 'studentsPage' }));
      return;
    }

    iss.newInvite.classes = [{ ...iss.selectedClass }];
    iss.newInvite.organisationName = orgStore.selectedOrg.name;
    iss.newInvites.push(iss.newInvite);
    iss.newInvite = { ...iss.cleanNewInvite };

    iss.getInvites(selectedOrganizationId);
    setInputsVisible(false);
  }, [iss, orgStore.selectedOrg.name, t, selectedOrganizationId]);

  const handleDeleteStudent = useCallback(
    (email: string) => {
      iss.newInvites = iss.newInvites.filter((invite) => invite.email !== email);
      !iss.newInvites.length && setInputsVisible(true);
    },
    [iss]
  );

  const handleModalClose = useCallback(() => {
    iss.isInviteModalOpen = false;
    iss.newInvite = { ...iss.cleanNewInvite };
    iss.newInvites = [];
    setInputsVisible(true);
  }, [iss]);

  return (
    <Modal
      open={iss.isInviteModalOpen}
      onClose={() => handleModalClose()}
      className='organization-modal invite-modal'
      title={t('InviteStudents', { ns: 'studentsPage' })}
      titleIcon={<SendIcon />}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: () => handleModalClose(),
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SendInvitation', { ns: 'studentsPage' }),
        onClick: () => {
          iss.submitInvitesAndUpdate(selectedOrganizationId);
          handleModalClose();
        },
        disabled: iss.numberOfInvitesLeft < 1 || !iss.newInvites.length || iss.isLoading,
      }}
    >
      <div className='modal-container'>
        {iss.numberOfInvitesLeft < 1 ? (
          <NoInvitesLeft />
        ) : (
          <>
            <div className='invite-form modal-body'>
              <Paragraph>
                <>{t('StudentsWillBeNotifiedToJoin', { ns: 'studentsPage', className: iss.selectedClass.name })}</>
              </Paragraph>
              <div className='invite-inputs'>
                {iss.newInvites.length ? (
                  <>
                    <NewInvitesList deleteStudent={handleDeleteStudent} />
                    {!inputsVisible ? (
                      <button
                        className='styled-button secondary'
                        onClick={() => setInputsVisible(true)}
                        disabled={iss.numberOfInvitesLeft < 1 || iss.numberOfInvitesLeft === iss.newInvites.length}
                      >
                        {t('AddStudent', { ns: 'studentsPage' })}
                      </button>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
                {inputsVisible ? (
                  <>
                    <div className='inputs-wrapper'>
                      <Input
                        label={t('Name', { ns: 'studentsPage' })}
                        value={iss.newInvite.name ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => (iss.newInvite.name = e.currentTarget.value)}
                      />
                      <Input
                        label={t('Email', { ns: 'studentsPage' })}
                        value={iss.newInvite.email ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEmailError('');
                          iss.newInvite.email = e.currentTarget.value;
                        }}
                      />
                      {emailError && <ErrorInfo errorMsg={emailError} />}
                    </div>
                    <button className='styled-button secondary' onClick={() => handleSaveStudent()} disabled={!isFormValid}>
                      {t('Save', { ns: 'general' })}
                    </button>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <NumberOfInvitesLeft />
          </>
        )}
      </div>
    </Modal>
  );
});

export default InviteStudentsModal;
